import React from 'react';
import {
	getCheckpoints,
	saveChecklist,
	saveCheckpoints,
} from 'api/checklists/checklistsAPI';
import useNotificationProvider from 'hooks/notificationBar/useNotificationProvider';
import { useEffect, useImperativeHandle, useRef, useState } from 'react';
import initCheckpoint from '../data/initCheckpoint';
import ChecklistAlertTypes from '../constants/checklistAlertTypes';
import { getCheckPointIndexes } from '../utils/getCheckpointIndexes';
import { cloneDeep } from 'lodash';
import { ChecklistInputFields } from '../constants/checklistInputTypes';

const useChecklistEditor = (
	checklist: Checklist | null,
	setChecklists: React.Dispatch<React.SetStateAction<Checklist[]>>,
	checklistType: ChecklistTypes,
	customerID: string,
	addressID: string,
	setOpenInEditMode: React.Dispatch<React.SetStateAction<boolean>>,
	saveRef: React.MutableRefObject<any>,
) => {
	//CHECKLISTS STATES
	const [checklistFields, setChecklistFields] = useState({
		[ChecklistInputFields.Name]: '',
		[ChecklistInputFields.Description]: '',
	});

	//CHECKPOINTS STATES
	const [initialCheckpoints, setInitialCheckpoints] = useState<Checkpoint[]>(
		[],
	);
	const [checkpoints, setCheckpoints] = useState<Checkpoint[]>([]);
	const [highlightedCheckpointID, setHighlightedCheckpointID] =
		useState<number>(0);

	//OTHER STATES
	const [error, setError] = useState(false);
	const [pending, setPending] = useState(true);

	const [alertType, setAlertType] = useState<ChecklistAlertTypes | null>(
		null,
	);

	const { notifySuccess, notifyError, notifyWarning } =
		useNotificationProvider();

	const minimizeAfterSave = useRef(false);

	const [inputValidation, setInputValidation] = useState({
		[ChecklistInputFields.Name]: {
			isError: false,
			errorMessage: 'Name cannot be empty',
			canSave: false,
		},
	});

	//GRAB CHECKPOINTS OF THE PASSED ON CHECKLIST FROM THE DATABASE:
	useEffect(() => {
		if (checklist) {
			setChecklistFields({
				[ChecklistInputFields.Name]: checklist.Name,
				[ChecklistInputFields.Description]: checklist.Description,
			});

			fetchCheckpoints();
		} else {
			setPending(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useImperativeHandle(saveRef, () => ({
		checkForUnsavedChanges: () => {
			if (checkUnsaved()) {
				setAlertType(ChecklistAlertTypes.unsavedWarning);
				return true;
			} else {
				return false;
			}
		},
	}));

	//---------------------HELPER FUNCTIONS START----------------------------//
	async function fetchCheckpoints() {
		setPending(true);
		try {
			if (!checklist) return;
			const checkpoints = await getCheckpoints(checklist.ChecklistID);
			setCheckpoints(checkpoints);
			setInitialCheckpoints(checkpoints);
		} catch (error) {
			notifyError('Error! Failed to fetch checkpoints');
			setError(true);
		} finally {
			setPending(false);
		}
	}
	const inputValidationHelpers = {
		checkInputValidationErrors: () => {
			return (
				inputValidationHelpers.checkChecklistInputErrors() ||
				inputValidationHelpers.checkCheckpointInputErrors()
			);
		},
		checkChecklistInputErrors: () => {
			if (inputValidation[ChecklistInputFields.Name].isError) {
				return true;
			} else {
				// Edge case, when you create a new checklist:
				if (checklistFields[ChecklistInputFields.Name].length === 0) {
					setInputValidation((prev) => ({
						...prev,
						[ChecklistInputFields.Name]: {
							...prev[ChecklistInputFields.Name],
							isError: true,
						},
					}));
					return true;
				}
			}

			return false;
		},
		checkCheckpointInputErrors: () => {
			const _highlightedCheckpoint = getHighlightedCheckpoint();

			if (!_highlightedCheckpoint.Name) {
				return true;
			}
			if (_highlightedCheckpoint.ResponseType === 'Status') {
				if (
					_highlightedCheckpoint.Status.some((item) => item.isError)
				) {
					return true;
				}
			}
			if (
				_highlightedCheckpoint.Dropdowns.some(
					(item) => item.Name.length === 0,
				)
			) {
				return true;
			}
			return false;
		},
		resetChecklistInputValidation: () => {
			setInputValidation((prevState) => {
				return {
					...prevState,
					[ChecklistInputFields.Name]: {
						...prevState[ChecklistInputFields.Name],
						isError: false,
					},
				};
			});
		},
	};

	function getHighlightedCheckpoint() {
		const _highlightedCheckpoint = checkpoints.find(
			(checkpoint) => checkpoint.CheckpointID === highlightedCheckpointID,
		);

		return _highlightedCheckpoint || initCheckpoint;
	}
	function displayCheckpointTableMessage() {
		if (error) {
			return <h1>Error! Something went wrong</h1>;
		}
		if (checkpoints.length === 0) {
			return (
				<h1>
					No checkpoint added.
					<br /> Click '+ Checkpoint' to add checkpoints.
				</h1>
			);
		}
	}
	//RETURNS TRUE ON UNSAVED CHANGES:
	const checkUnsaved = () => {
		const hasDifferentChecklistName =
			checklist &&
			checklist.Name !== checklistFields[ChecklistInputFields.Name];
		const hasDifferentDescription =
			checklist &&
			checklist.Description !==
				checklistFields[ChecklistInputFields.Description];

		// WE NEED TO COMPARE THE CHECKPOINTS WITHOUT THE isSelected PROPERTY:
		const initialWithoutIsSelected = initialCheckpoints.map(
			({ isSelected, ...item }) => item,
		);
		const checkpointsWithoutIsSelected = checkpoints.map(
			({ isSelected, ...item }) => item,
		);
		const hasDifferentCheckpoints =
			JSON.stringify(initialWithoutIsSelected) !==
			JSON.stringify(checkpointsWithoutIsSelected);

		return (
			hasDifferentChecklistName ||
			hasDifferentDescription ||
			hasDifferentCheckpoints
		);
	};

	function updateChecklists(_checklist: Checklist) {
		if (checklist) {
			setChecklists((prevChecklists) =>
				prevChecklists.map((currentChecklist) =>
					currentChecklist.ChecklistID === _checklist.ChecklistID
						? _checklist
						: currentChecklist,
				),
			);
		} else {
			setChecklists((prevChecklists) => [_checklist, ...prevChecklists]);
			setChecklistFields({
				[ChecklistInputFields.Name]: '',
				[ChecklistInputFields.Description]: '',
			});
			setCheckpoints([]);
			setHighlightedCheckpointID(0);
		}
	}

	async function saveAllCheckpointEditorChanges(_checklistID: number) {
		// Saving checkpoints:
		const { highlightedCheckpointIndex, selectedCheckpointIndexes } =
			getCheckPointIndexes(checkpoints, highlightedCheckpointID);

		const savedCheckpoints = await saveCheckpoints(
			checkpoints,
			_checklistID,
		);

		const savedCheckpointsWithIsSelected: Checkpoint[] =
			savedCheckpoints.map((checkpoint: Checkpoint, index: number) => {
				return {
					...checkpoint,
					isSelected: selectedCheckpointIndexes.includes(index),
				};
			});

		if (highlightedCheckpointIndex !== -1)
			setHighlightedCheckpointID(
				savedCheckpoints[highlightedCheckpointIndex]?.CheckpointID,
			);
		setInitialCheckpoints(cloneDeep(savedCheckpoints));
		setCheckpoints(savedCheckpointsWithIsSelected);
	}

	//---------------------HELPER FUNCTIONS END----------------------------//

	//---------------------CHECKLISTS HANDLERS START----------------------------//
	const onHandleInput = (e: any) => {
		const { name, value } = e.target;
		setChecklistFields((prev) => ({
			...prev,
			[name]: value,
		}));
		if (name === ChecklistInputFields.Name) {
			setInputValidation((prev: any) => ({
				...prev,
				[name]: {
					...prev[name],
					isError: value.length === 0,
				},
			}));
		}
	};

	const alertTypesHandlers = {
		[ChecklistAlertTypes.unsavedWarning]: {
			onLeaveAfterSave: () => {
				minimizeAfterSave.current = true;
				saveAllChecklistEditorChanges();
			},
			onLeaveWithoutSaving: () => {
				setOpenInEditMode(false);
			},
			onCancel: () => {
				minimizeAfterSave.current = false;
				setAlertType(null);
				setPending(false);
			},
		},
		[ChecklistAlertTypes.assignedPatrolWarning]: {
			onProceedToSave: () => {
				saveAllChecklistEditorChanges();
			},
			onMoreInfo: () => {
				setAlertType(ChecklistAlertTypes.assignedPatrolInfo);
			},
			onCancel: () => {
				minimizeAfterSave.current = false;
				setAlertType(null);
				setPending(false);
			},
		},
		[ChecklistAlertTypes.assignedPatrolInfo]: {
			onBack: () => {
				setAlertType(ChecklistAlertTypes.assignedPatrolWarning);
			},
			onCancel: () => {
				minimizeAfterSave.current = false;
				setAlertType(null);
				setPending(false);
			},
		},
	};

	const onSaveChecklist = async () => {
		saveAllChecklistEditorChanges();
	};
	const onResetChecklist = () => {
		const { Name = '', Description = '' } = checklist || {};
		setChecklistFields({
			[ChecklistInputFields.Name]: Name,
			[ChecklistInputFields.Description]: Description,
		});
		setCheckpoints(initialCheckpoints);
		setHighlightedCheckpointID(initCheckpoint.CheckpointID);
		inputValidationHelpers.resetChecklistInputValidation();
	};
	//---------------------CHECKLIST HANDLERS END----------------------------//

	//--------------------- SAVE FUNCTION----------------------------//
	async function saveAllChecklistEditorChanges() {
		if (inputValidationHelpers.checkInputValidationErrors()) {
			notifyError('Please fill all required fields');
			return;
		}
		if (alertType !== ChecklistAlertTypes.assignedPatrolWarning) {
			if ((checklist?.AssignedPatrols?.length ?? 0) > 0) {
				setAlertType(ChecklistAlertTypes.assignedPatrolWarning);
				return;
			}
		}

		// Put the editor in a pending state
		setPending(true);
		setAlertType(null);

		notifyWarning('Saving');

		// Save Checklist
		let savedChecklist = null;
		try {
			savedChecklist = await saveChecklist({
				ChecklistID: checklist ? checklist.ChecklistID : -1,
				Name: checklistFields[ChecklistInputFields.Name],
				Description: checklistFields[ChecklistInputFields.Description],
				CustomerID:
					parseInt(customerID) === 0 ? null : parseInt(customerID),
				AddressID: parseInt(addressID),
				Type: checklist ? checklist.Type : checklistType,
			});
		} catch (error) {
			notifyError('CHECKLIST CREATION FAILED!');
			return;
		}

		await saveAllCheckpointEditorChanges(savedChecklist.ChecklistID);

		updateChecklists(savedChecklist);

		setAlertType(null);
		setPending(false);

		notifySuccess('Checklist saved successfully');

		if (minimizeAfterSave.current) {
			setOpenInEditMode(false);
		}
	}
	//--------------------- SAVE LOGIC END----------------------------//

	return {
		setHighlightedCheckpointID,
		checklistFields,
		checkpoints,
		setCheckpoints,
		highlightedCheckpointID,
		getHighlightedCheckpoint,
		pending,
		onResetChecklist,
		alertType,
		displayCheckpointTableMessage,
		setAlertType,
		onHandleInput,
		alertTypesHandlers,
		onSaveChecklist,
		inputValidation,
		setInputValidation,
		inputValidationHelpers,
	};
};

export default useChecklistEditor;
